import { z } from 'zod'
import type { JsonObject } from '../utils/json-schema-utils'

export enum NodeCategory {
  Agents = 'Agents',
  Embeddings = 'Embeddings',
  Prompts = 'Prompts',
  Tools = 'Tools',
  Data = 'Data',
  Skills = 'Skills',
}

export interface ToolTypeDeclaration {
  type: string
  label: string
  description: string
  schema: JsonObject
}

export interface DisabledToolEntry {
  nodeId: string
  subTools?: string[]
}

// Sub-tool type ids from torqagent-api's DocumentCollectionToolNode.getToolTypes().
export const DOC_COLLECTION_SEARCH_SUB_TOOL = 'search_document'
export const DOC_COLLECTION_CODE_INTERPRETER_SUB_TOOL = 'codeInterpreter'

/** Teammate Builder parameter keys on the documentCollection tool. */
export const ENABLE_VECTOR_SEARCH_PARAM = 'enableVectorSearch'
export const ENABLE_CODE_INTERPRETER_PARAM = 'enableCodeInterpreter'

// These types are shared with the API repo (see packages/core/src/interface/langChain.ts)
export type ConditionOperator = 'truthy' | 'equal' | 'and' | 'or' | 'hasPermission'

export interface BaseCondition {
  op: ConditionOperator
}

export interface TruthyCondition extends BaseCondition {
  op: 'truthy'
  input: string
}

export interface EqualCondition extends BaseCondition {
  op: 'equal'
  input: string
  value: any
}

export interface AndCondition extends BaseCondition {
  op: 'and'
  conditions: SerializableCondition[]
}

export interface OrCondition extends BaseCondition {
  op: 'or'
  conditions: SerializableCondition[]
}

export interface HasPermissionCondition extends BaseCondition {
  op: 'hasPermission'
  permission: string
}

export type SerializableCondition = TruthyCondition | EqualCondition | AndCondition | OrCondition | HasPermissionCondition

export enum NodeInputType {
  Tool = 'Tool',
  string = 'string',
  BasePromptTemplate = 'BasePromptTemplate',
  BaseChatModel = 'BaseChatModel',
  number = 'number',
  options = 'options',
  BaseLanguageModel = 'BaseLanguageModel',
  json = 'json',
  BaseMemory = 'BaseMemory',
  VectorStoreRetriever = 'VectorStoreRetriever',
  BaseRetriever = 'BaseRetriever',
  VectorStore = 'VectorStore',
  file = 'file',
  css = 'css',
  TextSplitter = 'TextSplitter',
  password = 'password',
  boolean = 'boolean',
  Embeddings = 'Embeddings',
  slider = 'slider',
  documentCollection = 'documentCollection',
  radio = 'radio',
  subTeammates = 'subTeammates',
}

export interface OptionsInputOptions {
  name: string
  label: string
  description?: string
  /** Set when the option refers to a soft-deleted resource (e.g. a deleted Sub Teammate). */
  isDeleted?: boolean
}

export type CommonType = string | number | boolean | null

// Perhaps we could make inherited nodes for specific types some day
export interface NodeInput {
  name: string
  label: string
  type: NodeInputType
  description?: string
  subtext?: string
  disabled?: boolean
  hidden?: boolean
  list?: boolean
  optional?: boolean
  placeholder?: string
  default?: CommonType
  legacyDefault?: CommonType
  additionalParams?: boolean
  acceptVariable?: boolean
  showWhen?: SerializableCondition
  validationSchema?: JsonObject

  // File inputs
  maxFileCount?: number
  multipleFiles?: boolean
  fileType?: string[]

  // Options inputs
  options?: OptionsInputOptions[]
  isDynamic?: boolean
  dynamicUpdateFunction?: string
  dynamicUpdateFunctionArgs?: string[]
  overideOptions?: boolean
  supportsDynamicRefresh?: boolean
  allowCustomValue?: boolean
  dependsOnInputs?: string[]

  // String inputs
  rows?: number

  // Slider inputs
  min?: number
  max?: number
  step?: number
  excludeFromUserTools?: boolean
}

export interface NodeInputParam extends NodeInput {
  id: string
  showPromptImproveButton?: boolean
}

export interface NodeData {
  id: string
  name: string
  userToolId?: string
  category: NodeCategory
  inputs: NodeInputs

  /** @deprecated instead of stuffing this onto NodeData we should keep the data necessary for Teammate definition separate from the details of the Node configuration */
  label: string
  /** @deprecated instead of stuffing this onto NodeData we should keep the data necessary for Teammate definition separate from the details of the Node configuration */
  isLatest?: boolean
  /** Set by the backend's user tool enrichment: true when the referenced User Tool is soft-deleted. Existing references still run, but the user should replace it. */
  isDeleted?: boolean
  /** @deprecated instead of stuffing this onto NodeData we should keep the data necessary for Teammate definition separate from the details of the Node configuration */
  latestUserToolId?: string
  /** @deprecated instead of stuffing this onto NodeData we should keep the data necessary for Teammate definition separate from the details of the Node configuration */
  toolInputSchema?: any
  /** @deprecated instead of stuffing this onto NodeData we should keep the data necessary for Teammate definition separate from the details of the Node configuration */
  description: string
  /** @deprecated instead of stuffing this onto NodeData we should keep the data necessary for Teammate definition separate from the details of the Node configuration */
  inputParams?: NodeInputParam[]
  /** @deprecated instead of stuffing this onto NodeData we should keep the data necessary for Teammate definition separate from the details of the Node configuration */
  isDeprecated?: boolean
}

export type NodeInputs = { [key: string]: string | string[] | number | boolean | null }

/** Node (The actual nodes used in the flow editor) */
export interface Node {
  id: string
  data: NodeData
  selected?: boolean
  permitted?: boolean
}

export const TruthyConditionSchema = z.object({
  op: z.literal('truthy'),
  input: z.string(),
})

export const EqualConditionSchema = z.object({
  op: z.literal('equal'),
  input: z.string(),
  value: z.any(),
})

export const AndConditionSchema = z.object({
  op: z.literal('and'),
  conditions: z.array(z.lazy(() => SerializableConditionSchema)),
})

export const OrConditionSchema = z.object({
  op: z.literal('or'),
  conditions: z.array(z.lazy(() => SerializableConditionSchema)),
})

export const HasPermissionConditionSchema = z.object({
  op: z.literal('hasPermission'),
  permission: z.string(),
})

export const SerializableConditionSchema = z.discriminatedUnion('op', [
  TruthyConditionSchema,
  EqualConditionSchema,
  AndConditionSchema,
  OrConditionSchema,
  HasPermissionConditionSchema,
])

export const NodeParameterSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    description: z.string().optional(),
    subtext: z.string().optional(),
    type: z.nativeEnum(NodeInputType),
    disabled: z.boolean().optional(),
    hidden: z.boolean().optional(),
    list: z.boolean().optional(),
    optional: z.boolean().optional(),
    options: z.array(z.object({ name: z.string(), label: z.string(), description: z.string().optional() })).optional(),
    placeholder: z.string().optional(),
    default: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
    legacyDefault: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
    additionalParams: z.boolean().optional(),
    acceptVariable: z.boolean().optional(),
    showWhen: SerializableConditionSchema.optional(),
    supportsDynamicRefresh: z.boolean().optional(),
    dependsOnInputs: z.array(z.string()).optional(),
    allowCustomValue: z.boolean().optional(),
  })
  .passthrough()

export const AvailableNodeSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    description: z.string(),
    category: z.nativeEnum(NodeCategory),
    userToolId: z.string().optional(),
    inputs: z.array(NodeParameterSchema).optional(),
    permitted: z.boolean(),
    modelBlackList: z.array(z.string()).optional(),
    modelWhiteList: z.array(z.string()).optional(),
  })
  .passthrough()

/** Available Nodes (The objects returned from the /nodes endpoint) */
export interface AvailableNode {
  name: string
  label: string
  description: string
  category: NodeCategory
  userToolId?: string
  inputs?: NodeInput[]
  permitted?: boolean
  toolInputSchema?: any
  toolTypes?: ToolTypeDeclaration[]
  /** Runtime Tool.name when it differs from this node's `name` (WF-2328 chip-list discovery). */
  toolName?: string

  modelBlackList?: string[]
  modelWhiteList?: string[]
}

export interface Sidebar {
  mode: 'configure' | 'select' | null
  options: AvailableNode[] | NodeInputParam[] | unknown[] | null
  title: string | null
  subTitle: string | null
  focusItem: Node | null
  onSelect?: (node: AvailableNode | unknown) => void
}
